import z from "zod";

// Response from GET /tool-connectivity. `connected` is the org connection;
// `userConnected` is the per-user grant, sent only for Slack and only when true.
export const ToolConnectivityResponseSchema = z.object({
  integrations: z.record(z.string(), z.object({ connected: z.boolean(), userConnected: z.boolean().optional() })),
});

// Response from GET /tool-approval-prefs; the array is null for a user with no
// saved preferences.
export const ToolApprovalPrefsResponseSchema = z.object({
  approvedOperations: z.array(z.string()).nullable(),
});

// Response from GET /tool-actions. Kept loose: callers filter entries against
// the statuses they understand instead of failing the whole batch.
export const ChatToolActionsResponseSchema = z.object({
  actions: z
    .array(
      z.looseObject({
        id: z.string().optional(),
        status: z.string().optional(),
        // Whether the reading user may accept this one. Actions whose write
        // lands in the proposer's own mailbox or calendar are theirs alone to
        // approve; everyone else can still decline.
        approvable: z.boolean().optional(),
        // Present only for executed actions whose provider result has a URL
        // (e.g. Notion page/database creations).
        url: z.string().optional(),
        error: z.string().optional(),
        // RFC 3339, stamped when the action settled — a failed, rejected or
        // blocked action carries one too, despite the name.
        executedAt: z.string().optional(),
        // Set on a failed MCP action whose server rejected the member's own
        // connection; reconnecting is what fixes it.
        reconnect: z.boolean().optional(),
        // The tool-approval preference key this action is gated on.
        op: z.string().optional(),
        // Whether approve_always may turn that preference on from this card;
        // false for deletes, automation-driven chats and anyone but the proposer.
        alwaysApprovable: z.boolean().optional(),
        // The action ran without a card because the user's saved tool
        // permission approved it.
        autoApproved: z.boolean().optional(),
      })
    )
    .nullable(),
});

// Response from POST /tool-actions/{actionId}/confirm. Kept loose: callers
// validate `status` against the terminal statuses they understand.
export const ConfirmToolActionResponseSchema = z.looseObject({
  actionId: z.string().optional(),
  status: z.string().optional(),
  result: z.unknown().optional(),
  error: z.string().optional(),
});

// PUT /tool-approval-prefs body
export const UpdateToolApprovalPrefsInputSchema = z.object({
  approvedOperations: z.array(z.string()),
});

// Query params for GET /tool-actions
export const ChatToolActionsParamsSchema = z.object({
  chat: z.string(),
});

// Path params for POST /tool-actions/{actionId}/confirm
export const ConfirmToolActionParamsSchema = z.object({
  actionId: z.string(),
});

// POST /tool-actions/{actionId}/confirm body. approve_always runs the action and
// also turns on its preference key, so later ones skip the card.
export const ConfirmToolActionInputSchema = z.object({
  decision: z.enum(["approve", "approve_always", "reject"]),
  notifyAttendees: z.boolean(),
  editedParams: z.record(z.string(), z.unknown()).optional(),
});
